interface ExtendedMediaTrackConstraints extends MediaTrackConstraints {
  cursor?: 'always' | 'motion' | 'never';
}

interface ExtendedDisplayMediaStreamOptions extends DisplayMediaStreamOptions {
  preferCurrentTab?: boolean;
  video?: ExtendedMediaTrackConstraints;
}

export async function captureScreenImage(): Promise<Blob | null> {
  try {
    const width = Math.max(window.innerWidth || document.documentElement.clientWidth || 0, 640);
    const height = Math.max(window.innerHeight || document.documentElement.clientHeight || 0, 480);

    const constraints: ExtendedDisplayMediaStreamOptions = {
      video: {
        displaySurface: 'browser',
        cursor: 'never',
        width,
        height,
      },
      audio: false,
      preferCurrentTab: true,
    };

    const stream = await navigator.mediaDevices.getDisplayMedia(constraints);
    const [track] = stream.getVideoTracks();

    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.style.position = 'fixed';
    video.style.left = '-10000px';
    document.body.appendChild(video);

    await video.play();

    await new Promise<void>((resolve) => {
      if ('requestVideoFrameCallback' in video) {
        (
          video as HTMLVideoElement & {
            requestVideoFrameCallback: (cb: FrameRequestCallback) => number;
          }
        ).requestVideoFrameCallback(() => resolve());
      } else {
        // Fallback: wait a short moment for the first frame to render
        setTimeout(resolve, 100);
      }
    });

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No canvas context');

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    track.stop();
    video.remove();

    return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob ?? null), 'image/jpeg', 0.85));
  } catch (err) {
    console.error('[Screen Capture Error]', err);
    return null;
  }
}

export function isScreenCaptureSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}
